// components/NotificationDropdown.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Add this import
import {
  Box,
  Typography,
  IconButton,
  Badge,
  Popper,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider
} from '@mui/material';

const NotificationDropdown = ({ 
  icon, 
  badgeContent, 
  notifications, 
  title,
  emptyMessage = "No notifications",
  onNotificationClick // This will now be the handleNotificationClick from parent
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const popperRef = useRef(null);

  const handleToggle = () => {
    console.log('Toggle clicked, current state:', open);
    setOpen((prevOpen) => !prevOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        anchorRef.current && 
        !anchorRef.current.contains(event.target) &&
        popperRef.current && 
        !popperRef.current.contains(event.target)
      ) {
        console.log('Click outside detected, closing dropdown');
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return `${Math.floor(diffInHours / 24)}d ago`;
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    console.log('Notification clicked:', notification);
    console.log('Notification ID:', notification.notification_id);
    console.log('Notification Type:', notification.notification_type);
    console.log('Is read:', notification.is_read);
    console.log('onNotificationClick function exists:', !!onNotificationClick);
    
    if (onNotificationClick) {
      console.log('Calling onNotificationClick with ID and Type:', notification.notification_id, notification.notification_type);
      // await onNotificationClick(notification.notification_id, notification.notification_type);
      await onNotificationClick(notification);

    }
    
    // Close the dropdown after clicking
    setOpen(false);
  };

  return (
    <>
      <Badge 
        badgeContent={badgeContent} 
        color="error"
        sx={{
          '& .MuiBadge-badge': {
            fontSize: '0.6rem',
            height: '16px',
            minWidth: '16px',
          }
        }}
      >
        <IconButton
          ref={anchorRef}
          size="medium"
          onClick={handleToggle}
          sx={{
            p: 0.6,
            bgcolor: 'rgba(255,255,255,0.4)',
            borderRadius: '50%',
            boxShadow: '0 3px 6px rgba(0,0,0,0.25)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-1.5px)',
              boxShadow: '0 6px 10px rgba(0,0,0,0.35)',
              bgcolor: 'rgba(255,255,255,0.6)',
            },
          }}
        >
          {icon}
        </IconButton>
      </Badge>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        sx={{
          zIndex: 1300,
          mt: 1,
        }}
      >
        <Paper
          ref={popperRef}
          sx={{
            width: 320,
            maxHeight: 400,
            overflow: 'auto',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: '12px',
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
              {title}
              <Chip 
                label={badgeContent} 
                size="small" 
                color="primary" 
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
              />
            </Typography>
          </Box>

          <Divider />

          <List sx={{ p: 0 }}>
            {notifications && notifications.length > 0 ? (
              notifications.map((notification, index) => (
                <React.Fragment key={notification.notification_id}>
                  <ListItem 
                    alignItems="flex-start"
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.04)',
                        transform: 'translateX(2px)',
                      },
                      // Different background for unread notifications
                      bgcolor: !notification.is_read ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                      borderLeft: !notification.is_read ? '3px solid #1976d2' : '3px solid transparent',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: '0.85rem', 
                            lineHeight: 1.4,
                            fontWeight: !notification.is_read ? 600 : 400,
                            color: !notification.is_read ? 'text.primary' : 'text.secondary',
                          }}
                        >
                          {notification.message}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          sx={{ 
                            color: 'text.secondary', 
                            fontSize: '0.7rem',
                            mt: 0.5,
                            display: 'block'
                          }}
                        >
                          {formatTime(notification.created_at)}
                        </Typography>
                      }
                    />
                    {!notification.is_read && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          ml: 1,
                          mt: 0.5,
                          flexShrink: 0
                        }}
                      />
                    )}
                  </ListItem>
                  {index < notifications.length - 1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))
            ) : (
              <ListItem>
                <ListItemText>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      textAlign: 'center', 
                      color: 'text.secondary',
                      py: 2
                    }}
                  >
                    {emptyMessage}
                  </Typography>
                </ListItemText>
              </ListItem>
            )}
          </List>
        </Paper>
      </Popper>
    </>
  );
};

export default NotificationDropdown;